import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, fmt, fmtPct } from "@/lib/api";
import { toast } from "sonner";

const STRATEGIES = ["swing", "intraday", "positional", "breakout", "mean-reversion"];

export default function TradeIdeas() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialSymbol = (new URLSearchParams(location.search).get("s") || "NVDA").toUpperCase();
  const [symbol, setSymbol] = useState(initialSymbol);
  const [strategy, setStrategy] = useState("swing");
  const [capital, setCapital] = useState(100000);
  const [risk, setRisk] = useState(1.0);
  const [loading, setLoading] = useState(false);
  const [trade, setTrade] = useState(null);
  const [quickSymbols, setQuickSymbols] = useState([]);

  // Sync symbol from URL on navigation
  useEffect(() => {
    const s = new URLSearchParams(location.search).get("s");
    if (s) setSymbol(s.toUpperCase());
  }, [location.search]);

  // Populate QUICK chips dynamically from live market tickers + movers
  useEffect(() => {
    const load = async () => {
      try {
        const [tk, mv] = await Promise.all([
          api.get("/market/tickers"),
          api.get("/market/movers"),
        ]);
        const all = new Set();
        (tk.data.tickers || []).forEach(t => all.add(t.symbol));
        (mv.data.gainers || []).forEach(t => all.add(t.symbol));
        (mv.data.losers || []).forEach(t => all.add(t.symbol));
        setQuickSymbols(Array.from(all).slice(0, 24));
      } catch { /* silent */ }
    };
    load();
  }, []);

  const setSymbolAndUrl = (s) => {
    const upper = (s || "").toUpperCase();
    setSymbol(upper);
    navigate(`/trade-ideas?s=${encodeURIComponent(upper)}`, { replace: true });
  };

  const run = async () => {
    if (!symbol.trim()) { toast.error("Enter a symbol"); return; }
    setLoading(true); setTrade(null);
    try {
      const { data } = await api.post("/ai/trade-idea", {
        symbol: symbol.toUpperCase(), strategy, capital: Number(capital), risk_pct: Number(risk),
      });
      setTrade(data);
      toast.success(`Trade generated · ${data.direction} · ${data.conviction} conviction`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg;
      let suggestions = [];
      if (detail && typeof detail === "object") {
        msg = detail.message || `No data for ${symbol}`;
        suggestions = Array.isArray(detail.suggestions) ? detail.suggestions : [];
      } else {
        msg = detail || `No data for ${symbol} — try a different symbol`;
      }
      if (suggestions.length) {
        const s = suggestions.slice(0, 3).map((x) => x.symbol).filter(Boolean).join(", ");
        toast.error(`${msg} · Did you mean: ${s}?`, { duration: 6000 });
      } else {
        toast.error(msg);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="trade-ideas-page">
      <div className="border-b border-[color:var(--border-default)] pb-4 mb-6">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">◆ AI TRADE ENGINE</div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">Institutional Trade Setups</h1>
        <p className="text-[color:var(--text-secondary)] text-sm mt-2 max-w-2xl">
          Kelly-sized, risk-defined trades with entry, stop, targets, expected value, and invalidation logic.
        </p>
      </div>

      {/* Inputs */}
      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-4 mb-6" data-testid="trade-inputs">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Field label="SYMBOL">
            <input data-testid="trade-symbol" value={symbol} onChange={e => setSymbolAndUrl(e.target.value)} className="w-full bg-[color:var(--bg-surface)] border border-[color:var(--border-default)] px-3 py-2 text-sm font-mono-data text-white focus:outline-none focus:border-[color:var(--border-focus)]" />
          </Field>
          <Field label="STRATEGY">
            <select data-testid="trade-strategy" value={strategy} onChange={e => setStrategy(e.target.value)} className="w-full bg-[color:var(--bg-surface)] border border-[color:var(--border-default)] px-3 py-2 text-sm font-mono-data text-white focus:outline-none focus:border-[color:var(--border-focus)]">
              {STRATEGIES.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
          </Field>
          <Field label="CAPITAL (USD)">
            <input data-testid="trade-capital" type="number" value={capital} onChange={e => setCapital(e.target.value)} className="w-full bg-[color:var(--bg-surface)] border border-[color:var(--border-default)] px-3 py-2 text-sm font-mono-data text-white focus:outline-none focus:border-[color:var(--border-focus)]" />
          </Field>
          <Field label="RISK % PER TRADE">
            <input data-testid="trade-risk" type="number" step="0.1" value={risk} onChange={e => setRisk(e.target.value)} className="w-full bg-[color:var(--bg-surface)] border border-[color:var(--border-default)] px-3 py-2 text-sm font-mono-data text-white focus:outline-none focus:border-[color:var(--border-focus)]" />
          </Field>
          <button data-testid="generate-trade-btn" onClick={run} disabled={loading} className="bg-white text-black px-4 py-2 text-xs font-mono-data tracking-widest hover:opacity-90 disabled:opacity-50 transition-colors">
            {loading ? "COMPUTING…" : "GENERATE →"}
          </button>
        </div>
        <div className="flex flex-wrap gap-1 mt-3" data-testid="quick-symbols">
          {(quickSymbols.length ? quickSymbols : ["AAPL","NVDA","TSLA","MSFT","BTC-USD","GC=F","RELIANCE.NS","SPY"]).map(s => (
            <button key={s} data-testid={`quick-${s}`} onClick={() => setSymbolAndUrl(s)} className={`text-[10px] font-mono-data px-2 py-1 border transition-colors ${symbol === s ? "border-gold text-gold" : "border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)] hover:text-white"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="border border-dashed border-[color:var(--border-default)] p-16 text-center font-mono-data text-xs text-[color:var(--text-muted)]" data-testid="trade-loading">Computing Kelly-optimized setup…</div>}
      {trade && <TradeCard t={trade} />}
      {!trade && !loading && (
        <div className="border border-dashed border-[color:var(--border-default)] p-16 text-center" data-testid="trade-empty">
          <div className="font-serif-elite text-2xl text-[color:var(--text-secondary)]">Ready to generate.</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-2 font-mono-data">Enter a symbol above and click GENERATE.</div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[9px] font-mono-data tracking-widest text-[color:var(--text-muted)] mb-1">{label}</div>
      {children}
    </div>
  );
}

function TradeCard({ t }) {
  const dirColor = t.direction === "LONG" ? "text-bull" : "text-bear";
  const dirBg = t.direction === "LONG" ? "bg-[#32D74B]/10" : "bg-[#FF3B30]/10";
  const ideaId = t._idea_id;
  const [action, setAction] = useState(null);          // last chosen action
  const [posting, setPosting] = useState(null);        // action currently posting
  const [regimeCtx, setRegimeCtx] = useState(null);
  const [regimeLoading, setRegimeLoading] = useState(false);

  // Reset actions when a new idea (different id) is loaded.
  useEffect(() => { setAction(null); setPosting(null); }, [ideaId]);

  // Fetch signal intelligence whenever the idea changes.  This ONE call powers
  // the full institutional evidence panel — no additional LLM, no extra latency.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!t?.symbol) return;
      setRegimeLoading(true);
      try {
        const params = new URLSearchParams({
          symbol: t.symbol,
          ...(t.strategy_name ? { strategy: t.strategy_name } : {}),
          ...(t.win_probability_pct != null ? { confidence: String(t.win_probability_pct) } : {}),
          ...(t.direction ? { direction: t.direction } : {}),
          ...(t.risk_reward_ratio != null ? { risk_reward: String(t.risk_reward_ratio) } : {}),
          ...(t._live_quote?.volume != null ? { volume: String(t._live_quote.volume) } : {}),
          ...(t._idea_id ? { trade_idea_id: t._idea_id } : {}),
        });
        const r = await api.get(`/ai/trade-idea/signal-intelligence?${params.toString()}`);
        if (!cancelled) setRegimeCtx(r.data);
      } catch (e) {
        if (!cancelled) setRegimeCtx(null);
      } finally {
        if (!cancelled) setRegimeLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [t?.symbol, t?.strategy_name, t?.win_probability_pct, t?.direction, t?.risk_reward_ratio, t?._live_quote?.volume]);

  const recordAction = async (a) => {
    if (!ideaId) {
      toast.error("Idea id missing — cannot record action");
      return;
    }
    setPosting(a);
    try {
      await api.post(`/ai/trade-idea/${ideaId}/action`, { action: a });
      setAction(a);
      const label = a === "accepted" ? "Accepted" : a === "saved" ? "Saved" : "Ignored";
      toast.success(`${label} · logged to learning framework`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to record ${a}`);
    } finally {
      setPosting(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="trade-result">
      {t._demo && (
        <div className="border border-[#FFD700]/50 bg-[#FFD700]/5 px-4 py-2 text-[11px] font-mono-data text-gold" data-testid="demo-badge">
          ◆ DEMO OUTPUT — deterministic fallback; retry for full AI synthesis.
        </div>
      )}

      {/* Live Signal Intelligence — institutional evidence bundle. READ-ONLY. */}
      <SignalIntelligenceCard ctx={regimeCtx} loading={regimeLoading} />
      <div className={`border border-[color:var(--border-default)] ${dirBg} p-6`}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono-data text-2xl text-white">{t.symbol}</span>
              <span className={`text-xs font-mono-data tracking-widest ${dirColor} border border-current px-2 py-0.5`}>{t.direction}</span>
              <span className="text-[10px] font-mono-data text-gold border border-[#FFD700] px-2 py-0.5">{t.conviction} CONVICTION</span>
            </div>
            <div className="font-serif-elite text-2xl mt-2 text-white">{t.strategy_name}</div>
            <p className="text-sm text-[color:var(--text-secondary)] mt-1 max-w-2xl font-body leading-relaxed">{t.thesis}</p>
          </div>
          <div className="flex gap-4">
            <MetricBig label="WIN PROB" value={`${t.win_probability_pct}%`} />
            <MetricBig label="R:R" value={`${fmt(t.risk_reward_ratio, 2)}`} />
            <MetricBig label="EV" value={fmtPct(t.expected_value_pct)} tone={t.expected_value_pct >= 0 ? "bull" : "bear"} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-0 border border-[color:var(--border-default)]">
        <Cell label="ENTRY" value={fmt(t.entry)} tone="neutral" />
        <Cell label="STOP LOSS" value={fmt(t.stop_loss)} tone="bear" />
        <Cell label="TARGET 1" value={fmt(t.target_1)} tone="bull" />
        <Cell label="TARGET 2" value={fmt(t.target_2)} tone="bull" />
        <Cell label="TARGET 3" value={fmt(t.target_3)} tone="bull" />
        <Cell label="TIMEFRAME" value={t.timeframe} tone="neutral" mono={false} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--border-default)]">
        <Cell label="KELLY %" value={`${fmt(t.kelly_fraction_pct)}%`} tone="gold" />
        <Cell label="POSITION SIZE" value={`$${fmt(t.recommended_position_size_usd, 0)}`} tone="neutral" />
        <Cell label="SHARES / QTY" value={fmt(t.recommended_shares, 0)} tone="neutral" />
        <Cell label="MAX RISK" value={`$${fmt(t.max_risk_usd, 0)}`} tone="bear" />
        <Cell label="SHARPE EST." value={fmt(t.sharpe_estimate)} tone="neutral" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[color:var(--border-default)]">
        <div className="p-4 border-r border-[color:var(--border-default)]">
          <div className="text-[10px] font-mono-data tracking-widest text-bull mb-3">ENTRY TRIGGERS</div>
          <ul className="space-y-1.5">
            {(t.entry_triggers || []).map((x, i) => <li key={i} className="text-sm text-white font-body">→ {x}</li>)}
          </ul>
        </div>
        <div className="p-4">
          <div className="text-[10px] font-mono-data tracking-widest text-bear mb-3">INVALIDATION TRIGGERS</div>
          <ul className="space-y-1.5">
            {(t.invalidation_triggers || []).map((x, i) => <li key={i} className="text-sm text-white font-body">→ {x}</li>)}
          </ul>
        </div>
      </div>

      <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] text-center pt-2">
        VaR (95%): {fmtPct(t.var_95_pct)} · Not financial advice · Use with proper risk management
      </div>

      {/* Learning Framework — user interaction row (Accept / Save / Ignore) */}
      <div
        className="border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-3 flex flex-wrap items-center justify-between gap-3"
        data-testid="trade-actions-row"
      >
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">
          ◆ AI LEARNING · YOUR CALL{" "}
          {ideaId ? (
            <span className="ml-2 text-gold" data-testid="trade-idea-id">{`id ${ideaId.slice(0, 8)}…`}</span>
          ) : (
            <span className="ml-2 text-bear">(id unavailable)</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <ActionButton
            testId="action-accept"
            label="Accept"
            hint="This setup fits my plan — I'd take it"
            tone="bull"
            active={action === "accepted"}
            busy={posting === "accepted"}
            disabled={!ideaId || !!posting}
            onClick={() => recordAction("accepted")}
          />
          <ActionButton
            testId="action-save"
            label="Save"
            hint="Interesting, watching for now"
            tone="gold"
            active={action === "saved"}
            busy={posting === "saved"}
            disabled={!ideaId || !!posting}
            onClick={() => recordAction("saved")}
          />
          <ActionButton
            testId="action-ignore"
            label="Ignore"
            hint="Not for me"
            tone="bear"
            active={action === "ignored"}
            busy={posting === "ignored"}
            disabled={!ideaId || !!posting}
            onClick={() => recordAction("ignored")}
          />
        </div>
      </div>
    </div>
  );
}

function ActionButton({ label, hint, tone, active, busy, disabled, onClick, testId }) {
  const toneBorder = tone === "bull" ? "border-bull" : tone === "bear" ? "border-bear" : "border-gold";
  const toneText = tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : "text-gold";
  const activeBg = tone === "bull" ? "bg-[#32D74B]/15" : tone === "bear" ? "bg-[#FF3B30]/15" : "bg-[#FFD700]/15";
  return (
    <button
      data-testid={testId}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      disabled={disabled}
      title={hint}
      className={`px-3 py-1.5 border ${toneBorder} ${toneText} text-[11px] font-mono-data tracking-widest transition-colors ${
        active ? activeBg : "hover:bg-[color:var(--bg-hover)]"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      {busy ? "…" : active ? `✓ ${label.toUpperCase()}` : label.toUpperCase()}
    </button>
  );
}

function MetricBig({ label, value, tone }) {
  const c = tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : "text-white";
  return (
    <div>
      <div className="text-[9px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className={`font-mono-data text-2xl ${c}`}>{value}</div>
    </div>
  );
}

function Cell({ label, value, tone, mono = true }) {
  const c = tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : tone === "gold" ? "text-gold" : "text-white";
  return (
    <div className="p-3 border-r border-[color:var(--border-default)] last:border-r-0">
      <div className="text-[9px] font-mono-data tracking-widest text-[color:var(--text-muted)] mb-1">{label}</div>
      <div className={`${mono ? "font-mono-data" : "font-body"} text-lg ${c}`}>{value}</div>
    </div>
  );
}


// ============================================================================
// RegimeIntelligenceCard — historical evidence layer for a fresh trade idea.
// READ-ONLY.  Never fabricates probabilities: if the sample floor isn't met,
// it says "Collecting historical evidence." verbatim.
// ============================================================================
function RegimeIntelligenceCard({ ctx, loading }) {
  if (loading && !ctx) {
    return (
      <div className="border border-[color:var(--border-default)] p-3 text-[11px] font-mono-data text-[color:var(--text-muted)]" data-testid="regime-intel-loading">
        Loading regime intelligence…
      </div>
    );
  }
  if (!ctx) return null;

  const cr = ctx.current_regime || {};
  const regimeDisplay = (cr.display || "unknown").toUpperCase();
  const regimeTone = regimeDisplay === "BULL" ? "text-bull border-bull"
                    : regimeDisplay === "BEAR" ? "text-bear border-bear"
                    : regimeDisplay === "SIDEWAYS" ? "text-gold border-gold"
                    : "text-[color:var(--text-muted)] border-[color:var(--border-default)]";
  return (
    <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-4 space-y-3" data-testid="regime-intel-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[10px] font-mono-data tracking-widest text-gold">
          ◆ REGIME-AWARE HISTORICAL EVIDENCE · READ-ONLY
        </div>
        <div className={`px-2 py-0.5 border ${regimeTone} text-[11px] font-mono-data tracking-widest`} data-testid="regime-intel-current">
          MARKET REGIME · {regimeDisplay}
          {cr.deviation_pct != null && (
            <span className="ml-2 text-[color:var(--text-muted)] normal-case">
              {cr.proxy_symbol} {cr.close} vs 50d {cr.sma_50} ({cr.deviation_pct > 0 ? "+" : ""}{cr.deviation_pct}%)
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <RegimeCell
          label="STRATEGY IN THIS REGIME"
          testId="regime-intel-strategy"
          stats={ctx.strategy_performance}
        />
        <RegimeCell
          label="SYMBOL IN THIS REGIME"
          testId="regime-intel-symbol"
          stats={ctx.symbol_performance}
        />
        <ConfidenceCell
          testId="regime-intel-confidence"
          data={ctx.confidence}
        />
      </div>
    </div>
  );
}

function RegimeCell({ label, testId, stats }) {
  if (!stats || stats.status === "no_strategy_specified" || stats.status === "no_symbol_specified") {
    return (
      <div className="border border-[color:var(--border-default)] p-3" data-testid={`${testId}-hidden`}>
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
        <div className="text-[11px] font-mono-data text-[color:var(--text-muted)] mt-2">—</div>
      </div>
    );
  }
  if (stats.status === "collecting_evidence") {
    return (
      <div className="border border-[color:var(--border-default)] p-3" data-testid={`${testId}-collecting`}>
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
        <div className="text-[11px] font-mono-data text-gold mt-2">Collecting historical evidence.</div>
        <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-1">
          {stats.n} sample{stats.n === 1 ? "" : "s"} on record · {stats.samples_needed} more needed
        </div>
      </div>
    );
  }
  const wr = stats.win_rate_pct;
  const wrTone = wr == null ? "text-white" : wr >= 55 ? "text-bull" : wr <= 45 ? "text-bear" : "text-gold";
  return (
    <div className="border border-[color:var(--border-default)] p-3" data-testid={`${testId}-sufficient`}>
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className={`font-mono-data text-2xl mt-1 ${wrTone}`} data-testid={`${testId}-win-rate`}>
        {wr == null ? "—" : `${wr}%`}
      </div>
      <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-1">
        {stats.n} resolved · {stats.wins}W / {stats.losses}L
        {stats.avg_risk_reward != null && ` · avg R:R ${stats.avg_risk_reward}`}
      </div>
    </div>
  );
}

function ConfidenceCell({ data, testId }) {
  if (!data || data.status === "no_confidence_specified") {
    return (
      <div className="border border-[color:var(--border-default)] p-3" data-testid={`${testId}-hidden`}>
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">AI CONFIDENCE</div>
        <div className="text-[11px] font-mono-data text-[color:var(--text-muted)] mt-2">—</div>
      </div>
    );
  }
  const band = data.band || "Medium";
  const tone = band === "High" ? "text-bull border-bull"
             : band === "Low" ? "text-bear border-bear"
             : "text-gold border-gold";
  const hist = data.status === "sufficient";
  return (
    <div className={`border p-3 ${tone.split(" ")[1]}`} data-testid={testId}>
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">AI CONFIDENCE</div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className={`font-mono-data text-2xl ${tone.split(" ")[0]}`} data-testid={`${testId}-band`}>{band.toUpperCase()}</div>
        <div className="text-xs font-mono-data text-[color:var(--text-secondary)]">
          {data.provided_pct != null ? `${data.provided_pct}%` : ""}
        </div>
      </div>
      {hist ? (
        <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-1">
          historical: {data.n} setups · {data.historical_win_rate_pct}% actual vs {data.avg_predicted_pct}% predicted
          <span className={Math.abs(data.calibration_gap_pct || 0) < 5 ? " text-bull"
                          : Math.abs(data.calibration_gap_pct || 0) < 15 ? " text-gold"
                          : " text-bear"}>
            {" "}({data.calibration_gap_pct > 0 ? "+" : ""}{data.calibration_gap_pct} gap)
          </span>
        </div>
      ) : (
        <div className="text-[10px] font-mono-data text-gold mt-1" data-testid={`${testId}-collecting`}>
          Collecting historical evidence.
        </div>
      )}
    </div>
  );
}



// ============================================================================
// RC2 · SignalIntelligenceCard — Institutional Trade Idea evidence bundle.
// Single API call powers the whole card.  Mobile-first responsive.
// READ-ONLY.  Never fabricates: any bucket below the sample floor renders
// "Collecting historical evidence." verbatim.
// ============================================================================
function SignalIntelligenceCard({ ctx, loading }) {
  if (loading && !ctx) {
    return (
      <div className="border border-[color:var(--border-default)] p-3 text-[11px] font-mono-data text-[color:var(--text-muted)]" data-testid="signal-intel-loading">
        Loading signal intelligence…
      </div>
    );
  }
  if (!ctx) return null;

  const rec = ctx.recommendation || "watch";
  const score = ctx.signal_score;
  const recTone = rec === "strong_buy" ? "border-bull text-bull bg-[#32D74B]/10"
                : rec === "buy" ? "border-bull text-bull"
                : rec === "avoid" ? "border-bear text-bear bg-[#FF3B30]/10"
                : "border-gold text-gold";
  const recLabel = rec === "strong_buy" ? "STRONG BUY"
                 : rec === "buy" ? "BUY"
                 : rec === "avoid" ? "AVOID" : "WATCH";
  const cr = ctx.regime || {};
  const trend = cr.trend || {};
  const mom = cr.momentum || {};
  const vol = cr.volatility || {};
  const liq = cr.liquidity || {};
  const breadth = cr.breadth || {};
  const regimeTone = cr.display === "bull" ? "border-bull text-bull"
                    : cr.display === "bear" ? "border-bear text-bear"
                    : cr.display === "sideways" ? "border-gold text-gold"
                    : "border-[color:var(--border-default)] text-[color:var(--text-muted)]";

  const components = ctx.components || {};
  const barConfig = [
    { key: "market",     label: "MARKET" },
    { key: "sector",     label: "SECTOR" },
    { key: "strategy",   label: "STRATEGY" },
    { key: "symbol",     label: "SYMBOL" },
    { key: "volume",     label: "VOLUME" },
    { key: "confidence", label: "CONFIDENCE" },
    { key: "risk",       label: "RISK / RR" },
  ];

  const positives = ctx.why?.positives || [];
  const negatives = ctx.why?.negatives || [];

  return (
    <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-4 space-y-4" data-testid="signal-intel-card">
      {/* Top row — Signal Score + Recommendation + Regime badge */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <div data-testid="signal-score-block">
            <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">SIGNAL SCORE</div>
            <div className="flex items-baseline gap-2">
              <div className="font-mono-data text-4xl text-white" data-testid="signal-score">{score ?? "—"}</div>
              <div className="font-mono-data text-xs text-[color:var(--text-muted)]">/ 100</div>
            </div>
          </div>
          <div className={`px-3 py-1 border ${recTone} font-mono-data text-sm tracking-widest`} data-testid="signal-recommendation">
            {recLabel}
          </div>
          {ctx.position_sizing && (
            <div className={`px-3 py-1 border ${recTone} font-mono-data text-sm tracking-widest`} data-testid="signal-position-sizing">
              SIZE · {ctx.position_sizing.suggested_pct_of_normal_risk}%
            </div>
          )}
        </div>
        <div className={`px-2 py-0.5 border ${regimeTone} text-[11px] font-mono-data tracking-widest`} data-testid="signal-regime-badge">
          MARKET · {(cr.display || "unknown").toUpperCase()}
          {trend.deviation_pct != null && (
            <span className="ml-2 text-[color:var(--text-muted)] normal-case">
              SPY {cr.close} · {trend.deviation_pct > 0 ? "+" : ""}{trend.deviation_pct}% vs 50d
            </span>
          )}
        </div>
      </div>

      {ctx.position_sizing?.note && (
        <div className="text-[10px] font-mono-data text-[color:var(--text-muted)]" data-testid="signal-position-note">
          ◆ SIZING · {ctx.position_sizing.note}{" "}
          <span className="opacity-70">({ctx.position_sizing.disclaimer})</span>
        </div>
      )}

      {/* Row 2 — Institutional key numbers strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-0 border border-[color:var(--border-default)]" data-testid="signal-key-numbers">
        <MiniKPI label="TREND" value={trend.strength?.replace("_", " ")} sub={trend.deviation_pct != null ? `${trend.deviation_pct > 0 ? "+" : ""}${trend.deviation_pct}%` : "—"} />
        <MiniKPI label="MOMENTUM" value={(mom.state || "").replace("_", " ")} sub={mom.return_20d_pct != null ? `20d ${mom.return_20d_pct}%` : "—"} />
        <MiniKPI label="VOLATILITY" value={vol.state} sub={vol.annualised_pct != null ? `${vol.annualised_pct}% ann` : "—"} />
        <MiniKPI label="LIQUIDITY" value={liq.state} sub={liq.ratio_5d_vs_60d != null ? `${liq.ratio_5d_vs_60d}× base` : "—"} />
        <MiniKPI label="BREADTH" value={breadth.state} sub={breadth.pct_above_sma20 != null ? `${breadth.pct_above_sma20}% above SMA20` : "—"} />
        <MiniKPI label="RISK LEVEL" value={ctx.risk_level} sub="R:R based" />
      </div>

      {/* Row 3 — Multi-layer confidence bars */}
      <div data-testid="signal-confidence-bars">
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)] mb-2">
          MULTI-LAYER CONFIDENCE
        </div>
        <div className="space-y-1.5">
          {barConfig.map(({ key, label }) => (
            <ConfidenceBar key={key} label={label} value={components[key]} testId={`bar-${key}`} />
          ))}
          <ConfidenceBar label="OVERALL" value={components.overall} testId="bar-overall" thick />
        </div>
      </div>

      {/* Row 4 — WHY? bullets */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border border-bull/40 p-3" data-testid="signal-why-positives">
          <div className="text-[10px] font-mono-data tracking-widest text-bull mb-1">WHY THIS TRADE</div>
          {positives.length === 0 ? (
            <div className="text-[11px] font-mono-data text-[color:var(--text-muted)]">Collecting historical evidence.</div>
          ) : (
            <ul className="text-[11px] font-mono-data text-white space-y-1">
              {positives.map((p, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`why-positive-${i}`}>
                  <span className="text-bull">✔</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border border-bear/40 p-3" data-testid="signal-why-negatives">
          <div className="text-[10px] font-mono-data tracking-widest text-bear mb-1">WHY BE CAREFUL</div>
          {negatives.length === 0 ? (
            <div className="text-[11px] font-mono-data text-[color:var(--text-muted)]">No material caveats detected.</div>
          ) : (
            <ul className="text-[11px] font-mono-data text-white space-y-1">
              {negatives.map((n, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`why-negative-${i}`}>
                  <span className="text-bear">✖</span>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Row 5 — historical evidence strip (strategy / symbol / sector) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="signal-history-strip">
        <HistoryCell label="STRATEGY WR IN REGIME" data={ctx.history?.strategy} testId="hist-strategy" />
        <HistoryCell label="SYMBOL WR IN REGIME" data={ctx.history?.symbol} testId="hist-symbol" />
        <HistoryCell label="SECTOR STRENGTH" data={ctx.history?.sector} testId="hist-sector" />
      </div>
    </div>
  );
}

function MiniKPI({ label, value, sub }) {
  const display = value ? String(value).toUpperCase() : "—";
  return (
    <div className="border-r border-b border-[color:var(--border-default)] p-2 last:border-r-0">
      <div className="text-[9px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className="font-mono-data text-sm text-white truncate">{display}</div>
      <div className="text-[9px] font-mono-data text-[color:var(--text-muted)] truncate">{sub}</div>
    </div>
  );
}

function ConfidenceBar({ label, value, testId, thick }) {
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value));
  const tone = value == null ? "bg-[color:var(--text-muted)]/30"
             : pct >= 70 ? "bg-bull"
             : pct >= 40 ? "bg-gold"
             : "bg-bear";
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <div className={`w-24 text-[10px] font-mono-data tracking-widest ${thick ? "text-white" : "text-[color:var(--text-muted)]"}`}>{label}</div>
      <div className="flex-1 border border-[color:var(--border-default)] h-3 bg-[color:var(--bg-base)]">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <div className={`w-10 text-right text-[11px] font-mono-data ${thick ? "text-white" : "text-[color:var(--text-secondary)]"}`}>
        {value == null ? "—" : `${value}`}
      </div>
    </div>
  );
}

function HistoryCell({ label, data, testId }) {
  if (!data) return null;
  const status = data.status;
  const showEmpty = ["no_strategy_specified", "no_symbol_specified", "no_sector_specified", "no_confidence_specified"].includes(status);
  if (showEmpty) {
    return (
      <div className="border border-[color:var(--border-default)] p-3" data-testid={`${testId}-hidden`}>
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
        <div className="text-[11px] font-mono-data text-[color:var(--text-muted)] mt-2">—</div>
      </div>
    );
  }
  if (status === "collecting_evidence") {
    return (
      <div className="border border-[color:var(--border-default)] p-3" data-testid={`${testId}-collecting`}>
        <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
        <div className="text-[11px] font-mono-data text-gold mt-2">Collecting historical evidence.</div>
        <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-1">
          {data.n || 0} sample{data.n === 1 ? "" : "s"}
          {data.samples_needed != null && ` · ${data.samples_needed} more needed`}
        </div>
      </div>
    );
  }
  const wr = data.win_rate_pct;
  const wrTone = wr == null ? "text-white" : wr >= 55 ? "text-bull" : wr <= 45 ? "text-bear" : "text-gold";
  return (
    <div className="border border-[color:var(--border-default)] p-3" data-testid={`${testId}-sufficient`}>
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className={`font-mono-data text-2xl mt-1 ${wrTone}`}>{wr == null ? "—" : `${wr}%`}</div>
      <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-1">
        {data.n} resolved · {data.wins}W / {data.losses}L
        {data.avg_risk_reward != null && ` · avg R:R ${data.avg_risk_reward}`}
      </div>
    </div>
  );
}
